import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MessageCircle, CalendarDays } from "lucide-react";
import { Button } from "../components/ui/button";
import { LOGO_URL, CONTACT, HERO_BG_IMAGES } from "../lib/content";

export const Hero = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % HERO_BG_IMAGES.length);
    }, 5500);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      // Cambiamos pb-16 por pb-32 en móvil para que los indicadores no choquen
     className="relative overflow-hidden bg-slate-900 text-white min-h-[92vh] flex items-center pt-28 pb-32 lg:pb-16"
    >
      {/* Rotating background images */}
      <div className="absolute inset-0" aria-hidden>
        <AnimatePresence mode="sync">
          <motion.div
            key={idx}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 1.6, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0"
          >
            <img
              src={HERO_BG_IMAGES[idx].src}
              alt={HERO_BG_IMAGES[idx].alt}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/92 via-slate-900/88 to-slate-900/95" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-slate-900/60" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Glows */}
      <div className="absolute -top-24 -left-24 w-[560px] h-[560px] glow-purple animate-pulse-glow" aria-hidden />
      <div className="absolute -bottom-40 -right-24 w-[520px] h-[520px] glow-orange animate-pulse-glow" aria-hidden />

      {/* Decorative braces */}
      <div className="pointer-events-none absolute top-24 right-4 md:right-16 text-[120px] md:text-[340px] leading-none font-mono text-white/5 select-none">{`{`}</div>
<div className="pointer-events-none absolute bottom-4 left-4 md:left-16 text-[120px] md:text-[340px] leading-none font-mono text-white/5 select-none">{`}`}</div>

      <div className="container mx-auto relative z-10 px-4 md:px-6">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-8"
          >
            <div className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-orange-400 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-orange-400" />
              <span>{`// custom software studio`}</span>
            </div>

            <h1 className="font-display font-black text-3xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tighter">
              Construimos
              <span className="text-orange-500 font-mono mx-1 sm:mx-2">{`{`}</span>
              soluciones
              <span className="text-orange-500 font-mono mx-1 sm:mx-2">{`}`}</span>
              <br className="hidden sm:block" />
              <span className="block sm:inline mt-2 sm:mt-0"> 
                que <span className="text-purple-400">resuelve</span> problemas reales.
              </span>
            </h1>

            <p className="mt-8 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
              En <span className="font-semibold text-white">JAR SMART.Studio</span> diseñamos y
              desarrollamos plataformas a medida, apps móviles, integraciones con IA y
              arquitecturas cloud para empresas que quieren moverse rápido sin romper nada.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button
                asChild
                data-testid="hero-cta-services"
                className="bg-purple-700 hover:bg-purple-600 text-white font-semibold rounded-full px-7 h-12 text-base group"
              >
                <a href="#servicios">
                  Ver servicios
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </Button>

              <Button
                asChild
                data-testid="hero-cta-whatsapp"
                variant="outline"
                className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full h-12 px-6"
              >
                <a href={CONTACT.whatsappUrl} target="_blank" rel="noreferrer">
                  <MessageCircle className="mr-2 h-4 w-4 text-green-400" />
                  Escríbenos por WhatsApp
                </a>
              </Button>

              <Button
                asChild
                data-testid="hero-cta-calendly"
                variant="ghost"
                className="text-white hover:bg-white/10 rounded-full h-12 px-6"
              >
              </Button>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { n: "+20", l: "proyectos" },
                { n: "+10", l: "empresas" },
                { n: "99.9%", l: "uptime promedio" },
              ].map((s) => (
                <div key={s.l} className="border-l border-white/10 pl-4">
                  <div className="font-display font-extrabold text-2xl md:text-3xl text-white">{s.n}</div>
                  <div className="text-xs md:text-sm text-slate-400 uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            // mb-24 da espacio para los puntitos en móvil. lg:mb-0 lo quita en PC.
            className="lg:col-span-4 flex justify-center lg:justify-end mt-12 lg:mt-0 mb-24 lg:mb-0"
          >
            <div className="relative w-full max-w-[260px] sm:max-w-[312px]"> {/* Control de ancho máximo */}
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-purple-600/30 via-transparent to-orange-500/30 blur-2xl" />
              <div className="relative bg-white/15 backdrop-blur-md rounded-2xl p-4 md:p-8 ring-1 ring-white/10 shadow-2xl">
                <img
                  src="../logo_JAR.png"
                  alt="JAR Smart.Studio logo"
                  className="w-full h-auto" // Asegura que guarde proporción
                />
              </div>
              <div className="absolute -bottom-3 -right-3 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 font-mono text-[10px] sm:text-xs text-orange-400 shadow-xl">
                {`> ready_to_ship`}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Slide indicators */}
        <div
          data-testid="hero-slide-indicators"
          className="absolute bottom-6 lg:bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-30"
        >
          {HERO_BG_IMAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`hero-slide-dot-${i}`}
              aria-label={`Ir a la imagen ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === idx ? "w-10 bg-orange-500" : "w-4 bg-white/25 hover:bg-white/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
